import { useEffect } from "react";
import { toast } from "react-toastify";

export const SESSION_EXPIRED_RECORDING_TOAST_ID = "session-expired-recording";

/**
 * EN-837 — while a recording/upload holds the authenticated shell open on an already-dead Supabase
 * session (app.tsx defers the router swap to the unauthenticated tree until the recorder goes idle,
 * so the live MediaRecorder isn't torn down mid-capture), the user is effectively signed out but the
 * shell stays interactive. Any NON-recorder action they take during that window silently fails auth
 * (401s) with no indication why. This surfaces that otherwise-silent state.
 *
 * The notice is intentionally PERSISTENT (`autoClose: false`) rather than a timed toast: the window
 * lasts as long as the recording does — minutes, or indefinitely for a wedged live capture — so a
 * toast that fades after a few seconds would leave the user silently signed-out again. It is shown
 * the instant the window opens and dismissed the instant it closes (recorder idles → the swap fires
 * and unmounts the shell, or the session re-authenticates and `active` goes false). Mirrors the
 * condition-driven persistent toast in useNetworkStatus.
 *
 * `active` is the deferred-window predicate, computed by the caller as
 * `sessionUserId === null && routableUserId !== null && recorderActive`. The recorderActive gate
 * matters: on a normal idle sign-out the swap fires in the same tick, so the id-only condition is
 * briefly true for one commit — gating on an active recorder means the notice only appears when a
 * recording is genuinely holding the shell, never as a flash on ordinary sign-out.
 */
export function useSessionExpiredRecordingToast(active: boolean): void {
    useEffect(() => {
        if (!active) {
            toast.dismiss(SESSION_EXPIRED_RECORDING_TOAST_ID);
            return;
        }
        if (!toast.isActive(SESSION_EXPIRED_RECORDING_TOAST_ID)) {
            toast.warning("Your session expired — your recording is still saving. Finish it, then sign back in.", {
                toastId: SESSION_EXPIRED_RECORDING_TOAST_ID,
                autoClose: false,
                closeOnClick: false,
            });
        }
    }, [active]);

    // Belt-and-suspenders: if the component using this hook unmounts while the notice is up (e.g. the
    // swap finally fires and tears the shell down), make sure the persistent toast doesn't linger.
    useEffect(() => () => toast.dismiss(SESSION_EXPIRED_RECORDING_TOAST_ID), []);
}
